// src/components/editor/PropertyPanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Type, Image, Palette, Move, Settings, Tag, Clock, Maximize, Square } from 'lucide-react';

const PropertyPanel = ({ 
  selectedElement, 
  onUpdateElement, 
  template, 
  onUpdateTemplate, 
  variables = [],
  onShowDynamicManager // Nouvelle prop pour ouvrir le gestionnaire dynamique
}) => {
  const [localValues, setLocalValues] = useState({});
  const [inputTimeouts, setInputTimeouts] = useState({});

  // Synchroniser les valeurs locales avec l'élément sélectionné
  useEffect(() => {
    if (selectedElement) {
      setLocalValues({
        content: selectedElement.content || '',
        fontSize: selectedElement.fontSize || 16,
        x: selectedElement.x || 0,
        y: selectedElement.y || 0,
        color: selectedElement.color || '#ffffff',
        backgroundColor: selectedElement.backgroundColor || 'transparent',
        fontFamily: selectedElement.fontFamily || 'sans-serif',
        fontWeight: selectedElement.fontWeight || 'normal',
        fontStyle: selectedElement.fontStyle || 'normal',
        zIndex: selectedElement.zIndex || 1,
        src: selectedElement.src || '',
        alt: selectedElement.alt || '',
        width: selectedElement.width || 100,
        height: selectedElement.height || 100,
        objectFit: selectedElement.objectFit || 'cover',
        isDynamic: selectedElement.isDynamic || false,
        dynamicTag: selectedElement.dynamicTag || '',
        enablePriceFormat: selectedElement.enablePriceFormat || false
      });
    }
  }, [selectedElement?.id]);

  // Fonction pour mettre à jour avec debounce
  const updateElementPropertyDebounced = useCallback((property, value) => {
    // Mettre à jour la valeur locale immédiatement
    setLocalValues(prev => ({ ...prev, [property]: value }));

    // Annuler le timeout précédent
    if (inputTimeouts[property]) {
      clearTimeout(inputTimeouts[property]);
    }

    // Créer un nouveau timeout
    const timeout = setTimeout(() => {
      if (onUpdateElement && selectedElement) {
        onUpdateElement(selectedElement.id, { [property]: value });
      }
    }, 300);

    setInputTimeouts(prev => ({ ...prev, [property]: timeout }));
  }, [onUpdateElement, selectedElement, inputTimeouts]);

  // Fonction pour mise à jour immédiate (checkboxes, selects)
  const updateElementPropertyImmediate = useCallback((property, value) => {
    setLocalValues(prev => ({ ...prev, [property]: value }));
    
    if (onUpdateElement && selectedElement) {
      onUpdateElement(selectedElement.id, { [property]: value });
    }
  }, [onUpdateElement, selectedElement]);

  // Nettoyer les timeouts
  useEffect(() => {
    return () => {
      Object.values(inputTimeouts).forEach(timeout => clearTimeout(timeout));
    };
  }, [inputTimeouts]);

  // Couleurs prédéfinies
  const presetColors = [
    '#ffffff', '#000000', '#d4af37', '#ff6b6b', '#4ecdc4', 
    '#45b7d1', '#96ceb4', '#feca57', '#ff9ff3', '#54a0ff'
  ];

  // Options de police
  const fontFamilies = [
    { value: 'sans-serif', label: 'Sans Serif' },
    { value: 'serif', label: 'Serif' },
    { value: 'monospace', label: 'Monospace' },
    { value: 'cursive', label: 'Cursive' },
    { value: 'fantasy', label: 'Fantasy' },
    { value: 'Arial, sans-serif', label: 'Arial' },
    { value: 'Georgia, serif', label: 'Georgia' },
    { value: 'Times New Roman, serif', label: 'Times New Roman' },
    { value: 'Helvetica, sans-serif', label: 'Helvetica' }
  ];

  if (!selectedElement) {
    return (
      <div className="full-height flex flex-col justify-center bg-white border-l">
        <div className="p-4">
          <h3 className="font-semibold mb-4">Propriétés</h3>
          <div className="text-center text-gray-500 py-8">
            <Settings className="h-8 w-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">Sélectionnez un élément</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="full-height flex flex-col bg-white border-l">
      <div className="p-4 border-b flex-shrink-0">
        <h3 className="font-semibold mb-2">Propriétés</h3>
        <p className="text-sm text-gray-600">{selectedElement.type} sélectionné</p>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6 custom-scrollbar">
        {/* Position */}
        <div>
          <h4 className="text-sm font-medium mb-2">Position</h4>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="number"
              value={localValues.x || 0}
              onChange={(e) => updateElementPropertyDebounced('x', parseInt(e.target.value) || 0)}
              className="px-2 py-1 border rounded text-sm"
              placeholder="X"
            />
            <input
              type="number"
              value={localValues.y || 0}
              onChange={(e) => updateElementPropertyDebounced('y', parseInt(e.target.value) || 0)}
              className="px-2 py-1 border rounded text-sm"
              placeholder="Y"
            />
          </div>
        </div>

        {/* Z-Index */}
        <div>
          <h4 className="text-sm font-medium mb-2">Ordre d'affichage</h4>
          <input
            type="number"
            value={localValues.zIndex || 1}
            onChange={(e) => updateElementPropertyDebounced('zIndex', parseInt(e.target.value) || 1)}
            className="w-full px-2 py-1 border rounded text-sm"
            min="1"
            placeholder="Z-Index"
          />
        </div>

        {/* Propriétés spécifiques au texte */}
        {selectedElement.type === 'text' && (
          <>
            <div>
              <h4 className="text-sm font-medium mb-2">Contenu</h4>
              <textarea
                value={localValues.content || ''}
                onChange={(e) => updateElementPropertyDebounced('content', e.target.value)}
                className="w-full px-2 py-1 border rounded text-sm"
                rows={3}
              />
              
              <div className="mt-2 p-2 bg-blue-50 rounded text-xs text-blue-700">
                <strong>Format prix :</strong> Utilisez "4€20" pour afficher 4 avec €20 en petit.
                Les centimes seront automatiquement stylés si activé.
              </div>
            </div>

            {/* Format Prix */}
            <div>
              <h4 className="text-sm font-medium mb-2 flex items-center">
                💰 Format Prix Avancé
              </h4>
              
              <div className="mb-3">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={selectedElement.enablePriceFormat || false}
                    onChange={(e) => updateElementPropertyImmediate('enablePriceFormat', e.target.checked)}
                  />
                  <span className="text-sm">Activer le format prix personnalisé</span>
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Permet de styliser différemment les euros et centimes (ex: 4€20)
                </p>
              </div>

              {/* Paramètres du format prix */}
              {selectedElement.enablePriceFormat && (
                <div className="space-y-4 p-3 bg-gray-50 rounded border-l-4 border-blue-400">
                  {/* Partie AVANT € (euros) */}
                  <div>
                    <h5 className="text-xs font-semibold text-gray-700 mb-2">Partie AVANT € (ex: "4")</h5>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Taille (%)</label>
                        <input
                          type="number"
                          value={selectedElement.priceFormat?.beforeEuroSize || 100}
                          onChange={(e) => updateElementPropertyImmediate('priceFormat', {
                            ...selectedElement.priceFormat,
                            beforeEuroSize: parseInt(e.target.value) || 100
                          })}
                          className="w-full px-2 py-1 border rounded text-xs"
                          min="20"
                          max="200"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Hauteur (%)</label>
                        <input
                          type="number"
                          value={selectedElement.priceFormat?.beforeEuroHeight || 0}
                          onChange={(e) => updateElementPropertyImmediate('priceFormat', {
                            ...selectedElement.priceFormat,
                            beforeEuroHeight: parseInt(e.target.value) || 0
                          })}
                          className="w-full px-2 py-1 border rounded text-xs"
                          min="-50"
                          max="50"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Symbole € */}
                  <div>
                    <h5 className="text-xs font-semibold text-gray-700 mb-2">Symbole €</h5>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Taille (%)</label>
                        <input
                          type="number"
                          value={selectedElement.priceFormat?.euroSize || 40}
                          onChange={(e) => updateElementPropertyImmediate('priceFormat', {
                            ...selectedElement.priceFormat,
                            euroSize: parseInt(e.target.value) || 40
                          })}
                          className="w-full px-2 py-1 border rounded text-xs"
                          min="20"
                          max="200"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Hauteur (%)</label>
                        <input
                          type="number"
                          value={selectedElement.priceFormat?.euroHeight || -20}
                          onChange={(e) => updateElementPropertyImmediate('priceFormat', {
                            ...selectedElement.priceFormat,
                            euroHeight: parseInt(e.target.value) || -20
                          })}
                          className="w-full px-2 py-1 border rounded text-xs"
                          min="-50"
                          max="50"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Partie APRÈS € (centimes) */}
                  <div>
                    <h5 className="text-xs font-semibold text-gray-700 mb-2">Partie APRÈS € (ex: "20")</h5>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Taille (%)</label>
                        <input
                          type="number"
                          value={selectedElement.priceFormat?.afterEuroSize || 40}
                          onChange={(e) => updateElementPropertyImmediate('priceFormat', {
                            ...selectedElement.priceFormat,
                            afterEuroSize: parseInt(e.target.value) || 40
                          })}
                          className="w-full px-2 py-1 border rounded text-xs"
                          min="20"
                          max="200"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Hauteur (%)</label>
                        <input
                          type="number"
                          value={selectedElement.priceFormat?.afterEuroHeight || -20}
                          onChange={(e) => updateElementPropertyImmediate('priceFormat', {
                            ...selectedElement.priceFormat,
                            afterEuroHeight: parseInt(e.target.value) || -20
                          })}
                          className="w-full px-2 py-1 border rounded text-xs"
                          min="-50"
                          max="50"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Typographie */}
            <div>
              <h4 className="text-sm font-medium mb-2">Typographie</h4>
              <div className="space-y-3">
                {/* Taille de police */}
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Taille (px)</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="range"
                      min="12"
                      max="72"
                      value={localValues.fontSize || 16}
                      onChange={(e) => updateElementPropertyDebounced('fontSize', parseInt(e.target.value))}
                      className="flex-1"
                    />
                    <span className="text-sm text-gray-600 w-10">{localValues.fontSize || 16}px</span>
                  </div>
                </div>

                {/* Police */}
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Police</label>
                  <select
                    value={localValues.fontFamily || 'sans-serif'}
                    onChange={(e) => updateElementPropertyImmediate('fontFamily', e.target.value)}
                    className="w-full px-2 py-1 border rounded text-sm"
                  >
                    {fontFamilies.map(font => (
                      <option key={font.value} value={font.value}>{font.label}</option>
                    ))}
                  </select>
                </div>

                {/* Gras et Italique */}
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Style</label>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        const newWeight = selectedElement.fontWeight === 'bold' ? 'normal' : 'bold';
                        updateElementPropertyImmediate('fontWeight', newWeight);
                      }}
                      className={`flex-1 px-2 py-1 text-xs border rounded transition-colors ${
                        selectedElement.fontWeight === 'bold' 
                          ? 'bg-blue-100 text-blue-700 border-blue-300' 
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <strong>Gras</strong>
                    </button>
                    <button
                      onClick={() => {
                        const newStyle = selectedElement.fontStyle === 'italic' ? 'normal' : 'italic';
                        updateElementPropertyImmediate('fontStyle', newStyle);
                      }}
                      className={`flex-1 px-2 py-1 text-xs border rounded transition-colors ${
                        selectedElement.fontStyle === 'italic' 
                          ? 'bg-blue-100 text-blue-700 border-blue-300' 
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <em>Italique</em>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Couleurs */}
            <div>
              <h4 className="text-sm font-medium mb-2">Couleurs</h4>
              <div className="space-y-3">
                {/* Couleur du texte */}
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Couleur du texte</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={localValues.color || '#ffffff'}
                      onChange={(e) => updateElementPropertyImmediate('color', e.target.value)}
                      className="w-8 h-8 border rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={localValues.color || '#ffffff'}
                      onChange={(e) => updateElementPropertyDebounced('color', e.target.value)}
                      className="flex-1 px-2 py-1 border rounded text-sm font-mono"
                      placeholder="#ffffff"
                    />
                  </div>
                </div>

                {/* Couleurs prédéfinies */}
                <div>
                  <label className="block text-xs text-gray-600 mb-2">Couleurs prédéfinies</label>
                  <div className="grid grid-cols-5 gap-2">
                    {presetColors.map(color => (
                      <button
                        key={color}
                        onClick={() => updateElementPropertyImmediate('color', color)}
                        className={`w-8 h-8 rounded border-2 ${
                          selectedElement.color === color ? 'border-blue-500' : 'border-gray-300'
                        }`}
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    ))}
                  </div>
                </div>

                {/* Couleur de fond */}
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Couleur de fond</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={selectedElement.backgroundColor === 'transparent' ? '#ffffff' : (selectedElement.backgroundColor || '#ffffff')}
                      onChange={(e) => updateElementPropertyImmediate('backgroundColor', e.target.value)}
                      className="w-8 h-8 border rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={localValues.backgroundColor || 'transparent'}
                      onChange={(e) => updateElementPropertyDebounced('backgroundColor', e.target.value)}
                      className="flex-1 px-2 py-1 border rounded text-sm font-mono"
                      placeholder="transparent"
                    />
                    <button
                      onClick={() => updateElementPropertyImmediate('backgroundColor', 'transparent')}
                      className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs hover:bg-gray-200"
                    >
                      Aucun
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Contenu dynamique */}
            <div>
              <h4 className="text-sm font-medium mb-2 flex items-center">
                <Tag className="h-4 w-4 mr-2" />
                Contenu Variable
              </h4>
              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={selectedElement.isDynamic || false}
                    onChange={(e) => updateElementPropertyImmediate('isDynamic', e.target.checked)}
                  />
                  <span className="text-sm">Variable temporelle</span>
                </label>
                
                {selectedElement.isDynamic && (
                  <div className="pl-6 space-y-2 border-l-2 border-orange-200">
                    <select
                      value={selectedElement.dynamicTag || ''}
                      onChange={(e) => updateElementPropertyImmediate('dynamicTag', e.target.value)}
                      className="w-full px-2 py-1 border rounded text-sm"
                    >
                      <option value="">Sélectionner une variable</option>
                      {variables.map(variable => (
                        <option key={variable.id} value={variable.id}>
                          {variable.name}
                        </option>
                      ))}
                    </select>
                    
                    {onShowDynamicManager && (
                      <button
                        onClick={onShowDynamicManager}
                        className="w-full px-3 py-2 bg-orange-100 text-orange-700 rounded hover:bg-orange-200 text-sm flex items-center justify-center space-x-2"
                      >
                        <Clock className="h-4 w-4" />
                        <span>Gérer les Règles</span>
                      </button>
                    )}

                    {/* Info sur le format prix pour les variables */}
                    {selectedElement.dynamicTag && 
                     variables.find(v => v.id === selectedElement.dynamicTag)?.category === 'prix' && (
                      <div className="p-2 bg-blue-50 rounded text-xs text-blue-700">
                        <strong>Astuce :</strong> Pour les prix avec centimes, utilisez le format "4€20" dans les règles temporelles.
                        La variable sera automatiquement remplacée.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Propriétés pour images et vidéos */}
        {(selectedElement.type === 'image' || selectedElement.type === 'video') && (
          <>
            <div>
              <h4 className="text-sm font-medium mb-2">Source</h4>
              <input
                type="text"
                value={localValues.src || ''}
                onChange={(e) => updateElementPropertyDebounced('src', e.target.value)}
                className="w-full px-2 py-1 border rounded text-sm"
                placeholder="Chemin du fichier"
              />
            </div>

            {selectedElement.type === 'image' && (
              <div>
                <h4 className="text-sm font-medium mb-2">Texte alternatif</h4>
                <input
                  type="text"
                  value={localValues.alt || ''}
                  onChange={(e) => updateElementPropertyDebounced('alt', e.target.value)}
                  className="w-full px-2 py-1 border rounded text-sm"
                  placeholder="Description de l'image"
                />
              </div>
            )}

            <div>
              <h4 className="text-sm font-medium mb-2">Dimensions</h4>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="number"
                  value={localValues.width || 0}
                  onChange={(e) => updateElementPropertyDebounced('width', parseInt(e.target.value) || 0)}
                  className="px-2 py-1 border rounded text-sm"
                  placeholder="Largeur"
                />
                <input
                  type="number"
                  value={localValues.height || 0}
                  onChange={(e) => updateElementPropertyDebounced('height', parseInt(e.target.value) || 0)}
                  className="px-2 py-1 border rounded text-sm"
                  placeholder="Hauteur"
                />
              </div>
            </div>

            {selectedElement.type === 'image' && (
              <div>
                <h4 className="text-sm font-medium mb-2">Ajustement</h4>
                <select
                  value={localValues.objectFit || 'cover'}
                  onChange={(e) => updateElementPropertyImmediate('objectFit', e.target.value)}
                  className="w-full px-2 py-1 border rounded text-sm"
                >
                  <option value="cover">Couvrir (cover)</option>
                  <option value="contain">Contenir (contain)</option>
                  <option value="fill">Remplir (fill)</option>
                  <option value="scale-down">Réduire (scale-down)</option>
                  <option value="none">Aucun (none)</option>
                </select>
              </div>
            )}
          </>
        )}
      </div>

      {/* Actions rapides */}
      <div className="p-4 border-t flex-shrink-0">
        <div className="text-xs text-gray-500 mb-2">
          ID: {selectedElement.id}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => {
              const duplicated = {
                ...selectedElement,
                id: `${selectedElement.type}-${Date.now()}`,
                x: selectedElement.x + 20,
                y: selectedElement.y + 20,
                zIndex: (selectedElement.zIndex || 0) + 1
              };
              // Cette action devrait être gérée par le parent
              console.log('Dupliquer élément:', duplicated);
            }}
            className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200"
          >
            Dupliquer
          </button>
          <button
            onClick={() => {
              // Cette action devrait être gérée par le parent
              console.log('Supprimer élément:', selectedElement.id);
            }}
            className="px-3 py-1 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200"
          >
            Supprimer
          </button>
        </div>
      </div>
    </div>
  );
};

export default PropertyPanel;